import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import RippleLoader from "../../../components/loader/RippleLoader";
import { Form, Formik } from "formik";
import ConfirmationModel from "../../../components/common/ConfirmationModel";
import {
  createTechnician,
  getAllTechnicianList,
  getCompanies,
  getOutlet,
} from "../../../redux/companies/slice";
import { useDispatch, useSelector } from "react-redux";
import { SelectWithLabel } from "../../../components/micro/input";
import { Chip } from "@mui/material";
import { Cancel } from "@mui/icons-material";

const EditPreferredModal = ({ onClose, companyId, values, modalData }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const isEditMode = modalData?.mode;

  const roleOptions = [
    { value: "primary", label: "Primary" },
    { value: "secondary", label: "Secondary" },
  ];

  //Selector
  const { techniciansList, outletList } = useSelector((store) => ({
    techniciansList: store?.companiesData?.getAllTechnicianListStatus?.data,
    outletList: store?.companiesData?.getOutletStatus?.data,
  }));

  useEffect(() => {
    dispatch(getAllTechnicianList({ search: "", limit: "all" }));
    dispatch(
      getOutlet({
        pageLimit: "all",
        search: "",
        company_id: companyId || "",
        sort_by: "name",
        sort_direction: "asc",
      })
    );
  }, []);

  const technicianOptions = techniciansList?.data?.map((technician) => ({
    label: technician?.full_name,
    value: technician?.id,
  }));

  const outletOptions = Array.isArray(outletList?.data)
    ? [
        { value: "all", label: "All" },
        ...outletList.data.map((technician) => ({
          label: technician?.name,
          value: technician?.id,
        })),
      ]
    : [];

  const handleFormSubmit = async (values) => {
    setIsLoading(true);
    const payload = {
      company_id: companyId,
      technician_id: values?.technician?.value,
      role: values?.role?.value,
      outlets: values?.outlets?.map((outlet) => outlet.value),
    };

    const action = modalData?.technician?.id
      ? createTechnician({ id: modalData?.technician?.id, ...payload })
      : createTechnician(payload);

    try {
      const res = await dispatch(action);
      if (res.type === "createTechnician/fulfilled") {
        setShowConfirmationModal(false);
        onClose();
        dispatch(getCompanies({ id: companyId }));
      }
    } catch (error) {
      console.error("Error submitting form: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteOutlet = (e, value, values, setFieldValue) => {
    e.stopPropagation();

    const updatedOutlets = values?.outlets?.filter(
      (outlet) => outlet.value !== value
    );

    setFieldValue("outlets", updatedOutlets);
  };

  return (
    <>
      <div className="model" onClick={onClose} style={{ zIndex: 1300 }}>
        <div
          className="bg-white items-center justify-center drop-shadow-lg rounded-lg w-full max-w-xl py-8 px-6 mx-auto relative"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <h4 className="font-Montserrat text-xl font-normal">
            {isEditMode === "edit"
              ? "Edit Preferred Technician"
              : "Add Preferred Technician"}
          </h4>
          {isLoading ? (
            <RippleLoader />
          ) : (
            <Formik
              enableReinitialize={true}
              initialValues={{
                technician: modalData?.technician?.id
                  ? {
                      value: modalData?.technician?.technician?.id,
                      label: modalData?.technician?.technician?.full_name,
                    }
                  : null,
                role: modalData?.technician?.id
                  ? roleOptions.find(
                      (option) => option.value === modalData?.technician?.role
                    )
                  : null,
                outlets: modalData?.technician?.id
                  ? modalData?.technician?.outlets?.map((outlet) => ({
                      value: outlet.id,
                      label: outlet.name,
                    }))
                  : [],
              }}
              validationSchema={Yup.object().shape({
                technician: Yup.object().required("Technician is required"),
                role: Yup.object().required("Role is required"),
                outlets: Yup.array().min(1, "At least one outlet is required"),
              })}
              onSubmit={(values) => {
                setFormValues(values);
                setShowConfirmationModal(true);
              }}
            >
              {({
                values,
                errors,
                touched,
                handleBlur,
                handleChange,
                handleSubmit,
                setFieldValue,
                resetForm,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <div className="mb-4 mt-3">
                    <SelectWithLabel
                      idFromName="technician"
                      placeholder="Select Technician"
                      options={technicianOptions}
                      value={values?.technician?.value}
                      setFieldValue={setFieldValue}
                      errors={errors.technician}
                      touched={touched.technician}
                      onChange={(event) => {
                        const selectedOption = technicianOptions?.find(
                          (option) => option.value === event.target.value
                        );
                        setFieldValue("technician", selectedOption);
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            zIndex: 1301,
                            maxHeight: "300px",
                            overflowY: "auto",
                          },
                        },
                      }}
                    />
                  </div>
                  <div className="mb-4 mt-3">
                    <SelectWithLabel
                      idFromName="role"
                      placeholder="Role"
                      options={roleOptions}
                      value={values?.role?.value}
                      setFieldValue={setFieldValue}
                      errors={errors.role}
                      touched={touched.role}
                      onChange={(event) => {
                        const selectedOption = roleOptions?.find(
                          (option) => option.value === event.target.value
                        );
                        setFieldValue("role", selectedOption);
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            zIndex: 1301,
                            maxHeight: "300px",
                            overflowY: "auto",
                          },
                        },
                      }}
                    />
                  </div>
                  <div className="mb-4 mt-3">
                    <SelectWithLabel
                      idFromName="outlet"
                      placeholder="Outlets"
                      options={outletOptions}
                      value={
                        (() => {
                          const outletValues = values?.outlets?.map((option) => option.value) || [];
                          const allOutletValues = outletOptions
                            .filter((opt) => opt.value !== "all")
                            .map((opt) => opt.value);
                          const isAllSelected =
                            allOutletValues.length > 0 &&
                            allOutletValues.every((val) => outletValues.includes(val));
                          return isAllSelected
                            ? ["all", ...outletValues]
                            : outletValues;
                        })()
                      }
                      setFieldValue={setFieldValue}
                      multiple
                      errors={errors.outlets}
                      touched={touched.outlets}
                      onChange={(event) => {
                        const selectedValues = event.target.value;
                        
                        const isAllSelected = selectedValues.includes("all");
                        
                        if (isAllSelected) {
                          const allOutlets = outletOptions.filter(
                            (option) => option.value !== "all"
                          );
                          setFieldValue("outlets", allOutlets);
                        } else {
                          const selectedOptions = outletOptions.filter(
                            (option) =>
                              selectedValues.includes(option.value) &&
                              option.value !== "all"
                          );
                          setFieldValue("outlets", selectedOptions);
                        }
                      }}
                      sx={{
                        "& .MuiSelect-select": {
                          minHeight: "auto !important",
                          padding: "8px 14px !important",
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "flex-start",
                          gap: "4px",
                        },
                        "& .MuiInputBase-root": {
                          minHeight: "auto",
                          padding: "0 !important",
                        },
                      }}
                      renderValue={(selected) => {
                        const isAllSelected = selected.includes("all");
                        const allOutletValues = outletOptions
                          .filter((opt) => opt.value !== "all")
                          .map((opt) => opt.value);
                        
                        const valuesToDisplay = isAllSelected
                          ? allOutletValues
                          : selected.filter((value) => value !== "all");
                        
                        return (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "4px",
                              maxHeight: "120px",
                              overflowY: "auto",
                            }}
                          >
                            {valuesToDisplay.map((value) => {
                              const selectedOption = outletOptions?.find(
                                (option) => option.value === value
                              );
                              return (
                                <Chip
                                  key={value}
                                  label={selectedOption?.label}
                                  sx={{
                                    marginRight: "4px",
                                    marginBottom: "4px",
                                    height: "auto",
                                    "& .MuiChip-deleteIcon": {
                                      color: "#F15B29",
                                      fontSize: "18px",
                                      "&:hover": {
                                        color: "#d14a1f",
                                      },
                                    },
                                  }}
                                  clickable
                                  variant="outlined"
                                  deleteIcon={
                                    <Cancel
                                      onMouseDown={(event) =>
                                        event.stopPropagation()
                                      }
                                    />
                                  }
                                  onDelete={(e) =>
                                    handleDeleteOutlet(
                                      e,
                                      value,
                                      values,
                                      setFieldValue
                                    )
                                  }
                                />
                              );
                            })}
                          </div>
                        );
                      }}
                    />
                  </div>

                  {/* Submit button */}
                  <div className="flex justify-end gap-3 mt-3 w-full">
                    <button
                      onClick={onClose}
                      type="button"
                      className=" text-themeBtn w-full flex items-center uppercase font-Montserrat justify-center px-6 py-1.5 rounded border border-themeBtn"
                    >
                      <span>Cancel</span>
                    </button>
                    <button
                      type="submit"
                      className="flex items-center w-full justify-center px-6 py-1.5 rounded font-Montserrat uppercase border-2 border-themeBtn  text-white bg-themeBtn"
                    >
                      <span>Confirm</span>
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
        {showConfirmationModal && (
          <div>
            <ConfirmationModel
              confirmText={isEditMode === "edit" ? "Edit" : "Create"}
              onClose={() => setShowConfirmationModal(false)}
              onConfirm={() => handleFormSubmit(formValues)}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default EditPreferredModal;
